import { useState, useEffect } from 'react';
import { loadBanksWithCurrentBalance } from '../utils/bank';
import {
  loadEntriesSummary,
  loadMonthlyChartData,
  loadDuePendingByRanges,
} from '../utils/financialDashboard';
import { STATUS } from '../constants/financial';

const useFinancialDashboardData = ({
  companyId,
  periodRange,
  payableRange,
  receivableRange,
  refreshKey = 0,
}) => {
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState([]);
  const [periodSummary, setPeriodSummary] = useState({ receitas: 0, despesas: 0, resultado: 0 });
  const [chartData, setChartData] = useState([]);
  const [duePending, setDuePending] = useState({ aPagar: 0, aReceber: 0 });

  useEffect(() => {
    if (!companyId || !periodRange) return;

    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [banksData, summary, chart, due] = await Promise.all([
          loadBanksWithCurrentBalance(companyId),
          loadEntriesSummary(companyId, periodRange),
          loadMonthlyChartData(companyId),
          loadDuePendingByRanges(companyId, payableRange, receivableRange),
        ]);

        if (cancelled) return;
        setBanks(banksData || []);
        setPeriodSummary(summary);
        setChartData(chart);
        setDuePending(due);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [
    companyId,
    periodRange?.from, periodRange?.to,
    payableRange?.from, payableRange?.to,
    receivableRange?.from, receivableRange?.to,
    refreshKey,
  ]);

  const activeBanks = banks.filter((b) => b.status === STATUS.ATIVO);
  // Saldo atual (Ajuste 06) = saldo inicial + parcelas pagas (currentBalance).
  const totalBalance = activeBanks.reduce(
    (sum, b) => sum + (Number(b.currentBalance) || 0),
    0
  );
  // Resultado = Lucro Líquido da DRE (receitas - despesas, onde despesas já
  // agregam dedução + custo + despesa), calculado no loadEntriesSummary.
  const result = periodSummary.resultado || 0;

  return {
    loading,
    banks: activeBanks,
    totalBalance,
    receitas: periodSummary.receitas,
    despesas: periodSummary.despesas,
    result,
    chartData,
    aPagar: duePending.aPagar,
    aReceber: duePending.aReceber,
  };
};

export default useFinancialDashboardData;
